// IntelligencePage.tsx - intelligence snapshot dashboard.
//
// The one page the family scope does not narrow: its nodes are evidence about
// the forge's own machinery — snapshot freshness, tool builds, the ecosystem —
// and nothing on it maps to a repository a family owns, so there is nothing
// honest to filter. It says so in the header chip (`useGlobalFamilyPage`)
// rather than let the chip imply these counts are one family's. The scope
// stays carried: Dependencies, linked from here, is scoped again.

import { useMemo } from 'react';
import {
  Activity,
  GitPullRequest,
  Network,
  Package,
  ServerCog,
  Terminal,
} from 'lucide-react';
import { Link } from 'react-router-dom';

import { PageHeader, queryFreshness } from '../components/pageHeader';
import { ErrorState, LoadingState } from '../components/state';
import { DEPENDENCIES_PATH } from './DependenciesPage';
import { useControlPlane } from '../hooks/useControlPlane';
import { useEcosystem, useToolBuildClusters } from '../hooks/useToolingEvidence';
import type { ControlPlaneSnapshot, EvidenceState } from '../api/types';
import { buildOperatorGraph } from './intelligenceGraphModel';
import {
  EvidencePanel,
  MetricCard,
  OperatorGraphConsole,
  StatePill,
  ToolBuildDossiers,
  useGraphViewState,
} from './intelligence';
import { useGlobalFamilyPage } from '../components/family/useGlobalFamilyPage';

import './page.css';
import './IntelligencePage.css';
import { IN_FLIGHT_PATH } from './pullRoomModel';
import { usePageTitle } from '../hooks/usePageTitle';

export function IntelligencePage(): JSX.Element {
  usePageTitle('Intelligence');
  useGlobalFamilyPage();
  const query = useControlPlane();

  return (
    <div className="page intelligence" data-testid="intelligence-page">
      <PageHeader
        title="Intelligence"
        subtitle="Operational snapshot of the repository graph and its evidence."
        className="intelligence__header"
        testId="intelligence-header"
        freshness={queryFreshness(query)}
        badges={
          query.data ? (
            <span data-testid="intelligence-snapshot-state">
              <StatePill state={snapshotState(query.data)} label="snapshot" />
            </span>
          ) : null
        }
      />
      {query.isLoading ? (
        <LoadingState variant="message" title="Loading intelligence snapshot." />
      ) : query.isError || !query.data ? (
        <ErrorState
          title="Could not load the intelligence snapshot."
          error={query.error}
          description={query.error ? undefined : 'Intelligence snapshot unavailable.'}
          onRetry={() => void query.refetch()}
          testId="intelligence-error"
        />
      ) : (
        <IntelligenceSnapshot snapshot={query.data} outOfDate={query.isStale} />
      )}
    </div>
  );
}

// Worst first: the header badge takes the worst state among its sources.
const STATE_RANK: EvidenceState[] = ['failed', 'missing', 'unknown', 'queued', 'fresh'];

export function evidenceStates(
  snapshot: ControlPlaneSnapshot
): EvidenceState[] {
  return [
    snapshot.localAuthority.state,
    snapshot.mirror.state,
    snapshot.artifacts.state,
    snapshot.mcp.state,
    snapshot.agentRuns.length > 0 ? 'fresh' : 'missing',
    snapshot.codegraph.state,
  ];
}

/** The snapshot badge, derived from the same evidence the page displays. */
export function snapshotState(snapshot: ControlPlaneSnapshot): EvidenceState {
  const states = evidenceStates(snapshot);
  return STATE_RANK.find((state) => states.includes(state)) ?? 'unknown';
}

function IntelligenceSnapshot({
  snapshot,
  outOfDate,
}: {
  snapshot: ControlPlaneSnapshot;
  /** The runner snapshot is past its refresh window: its counts are not evidence. */
  outOfDate: boolean;
}): JSX.Element {
  const ecosystem = useEcosystem();
  const toolClusters = useToolBuildClusters(10);
  // Filters, the search box and the picked node are in the URL: the graph a
  // person is looking at is a link, and Back undoes the node before it.
  const {
    filters: graphFilters,
    selectedNodeId,
    setFilters: setGraphFilters,
    selectNode,
  } = useGraphViewState();
  const runners = snapshot.runners.local;
  const runnerCapacityKnown = runners.state === 'fresh' && !outOfDate;
  const operatorGraph = useMemo(
    () =>
      buildOperatorGraph(
        snapshot,
        ecosystem.data ?? null,
        toolClusters.data?.clusters ?? [],
        graphFilters,
        selectedNodeId
      ),
    [ecosystem.data, graphFilters, selectedNodeId, snapshot, toolClusters.data]
  );

  return (
    <>
      <div className="intelligence__status-strip">
        <MetricCard
          icon={<GitPullRequest size={18} aria-hidden="true" />}
          label="Open pull requests"
          to={IN_FLIGHT_PATH}
          value={snapshot.summary.openPrCount}
          detail={`${snapshot.summary.missingCheckPrCount} missing checks`}
          state={
            snapshot.summary.missingCheckPrCount > 0 ? 'missing' : 'fresh'
          }
        />
        <MetricCard
          icon={<ServerCog size={18} aria-hidden="true" />}
          label="Runners"
          to="/runners"
          value={runnerCapacityKnown ? runners.onlineRunners : '—'}
          detail={
            runnerCapacityKnown
              ? `${runners.offlineRunners} offline`
              : runners.state === 'fresh'
                ? 'Runner snapshot out of date'
                : runners.state === 'unknown'
                  ? 'Runner capacity unknown'
                  : 'Runner capacity unavailable'
          }
          state={
            runners.state !== 'fresh'
              ? runners.state
              : outOfDate
                ? 'unknown'
                : runners.offlineRunners > 0
                  ? 'failed'
                  : 'fresh'
          }
        />
        <MetricCard
          icon={<Package size={18} aria-hidden="true" />}
          label="Artifacts"
          value={snapshot.artifacts.latestRelease.artifactCount}
          detail={
            snapshot.artifacts.latestRelease.artifactCount > 0
              ? 'in the latest release'
              : snapshot.artifacts.absenceIsSuccess
                ? 'none expected'
                : 'none recorded yet'
          }
          state={snapshot.artifacts.state}
        />
        <MetricCard
          icon={<Network size={18} aria-hidden="true" />}
          label="Graph"
          value={operatorGraph.nodes.length}
          detail={`${snapshot.codegraph.indexedSymbols} symbols · ${snapshot.toolBuild.clusterCount} tool clusters`}
          state={snapshot.codegraph.state}
        />
      </div>

      <section className="page__section" aria-labelledby="intelligence-graph">
        <div className="intelligence__section-head">
          <h2 className="page__section-title" id="intelligence-graph">
            Operator Graph
          </h2>
          <span className="page__pill">{operatorGraph.nodes.length} nodes</span>
          <span className="page__pill">{operatorGraph.edges.length} edges</span>
          <span className="page__pill">{operatorGraph.clusters.length} clusters</span>
          <Link className="page__pill" to={DEPENDENCIES_PATH}>
            Dependencies view
          </Link>
        </div>
        <OperatorGraphConsole
          graph={operatorGraph}
          filters={graphFilters}
          onFiltersChange={setGraphFilters}
          onSelectNode={selectNode}
        />
        <ToolBuildDossiers
          clusters={toolClusters.data?.clusters ?? []}
          summaryClusters={snapshot.toolBuild.topClusters}
          unavailable={toolClusters.isError ? toolClusters.error.message : null}
        />
      </section>

      <section className="page__section" aria-labelledby="intelligence-health">
        <h2 className="page__section-title" id="intelligence-health">
          Evidence snapshot
        </h2>
        <div className="intelligence__evidence-grid">
          <EvidencePanel
            icon={<Network size={18} aria-hidden="true" />}
            title="Mirror"
            state={snapshot.mirror.state}
            body={snapshot.mirror.divergence.reason}
          />
          <EvidencePanel
            icon={<Package size={18} aria-hidden="true" />}
            title="Artifacts"
            state={snapshot.artifacts.state}
            body={snapshot.artifacts.latestRelease.reason}
          />
          <EvidencePanel
            icon={<Activity size={18} aria-hidden="true" />}
            title="MCP"
            state={snapshot.mcp.state}
            body={`${snapshot.mcp.toolCount} tools, ${snapshot.mcp.liveBackedTools.length} live-backed`}
          />
          <EvidencePanel
            icon={<Terminal size={18} aria-hidden="true" />}
            title="Agent Runs"
            state={snapshot.agentRuns.length > 0 ? 'fresh' : 'missing'}
            body={`${snapshot.agentRuns.length} recorded run(s)`}
          />
          <EvidencePanel
            icon={<Network size={18} aria-hidden="true" />}
            title="Codegraph"
            state={snapshot.codegraph.state}
            body={snapshot.codegraph.reason}
          />
        </div>
      </section>
    </>
  );
}
